import React, { useState, useEffect, useContext, useMemo } from "react";
import ThemeMaker from "../../thememaker";
import DynamicTable from "../../common/DynamicTable";
import { httpService } from "../../../services/httpService";
import { TokenContext } from "../../../context/TokenContext";
import { FaEye } from "react-icons/fa";

const ReminderHistoryTable = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // ---------- Menu items ----------
  const menuItems = [
    { key: "all", label: "All Messages" },
    {
      key: "online_offline_booking",
      label: "Online/Offline Booking Confirmation",
    },
    { key: "online_booking_rejection", label: "Online Booking Rejection" },
    { key: "pos_invoice", label: "POS Invoice" },
    { key: "01_booking_reschedule_with_form", label: "Reschedule Changes" },
    { key: "02_order_completion", label: "Service Completion" },
  ];

  const [selectedKey, setSelectedKey] = useState("all");

  const { decodedData } = useContext(TokenContext);
  const vendorId =
    decodedData?.data?.user?.vendor_id || localStorage.getItem("vendorId");

  useEffect(() => {
    if (!vendorId) {
      setLoading(false);
      setError("Vendor not logged in");
      return;
    }

    const fetchReminders = async () => {
      try {
        const response = await httpService.get(
          `/notification/getreminderhistory/${vendorId}`,
        );
        const data = response?.data?.data || response?.data || [];
        setReminders(data);
      } catch (err) {
        console.error("Failed to fetch reminder history:", err);
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReminders();
  }, [vendorId]);

  // ---------- Filtered data based on selected menu item ----------
  const filteredData = useMemo(() => {
    if (selectedKey === "all") return reminders;

    switch (selectedKey) {
      case "online_offline_booking":
        return reminders.filter(
          (r) => r.campaign_name === "03_order_confirmation",
        );
      case "pos_invoice":
        return reminders.filter((r) => r.campaign_name === "pos");
      default:
        return reminders.filter((r) => r.campaign_name === selectedKey);
    }
  }, [reminders, selectedKey]);

  // ---------- Statistics ----------
  const stats = useMemo(() => {
    const total = filteredData.length;
    const sent = filteredData.filter(
      (r) =>
        (r.status || "").toLowerCase() === "sent" ||
        (r.status || "").toLowerCase() === "completed",
    ).length;
    const failed = total - sent;
    return { total, sent, failed };
  }, [filteredData]);

  // Get current filter label for header
  const currentFilterLabel = useMemo(() => {
    const found = menuItems.find((item) => item.key === selectedKey);
    return found ? found.label : "All Messages";
  }, [selectedKey]);

  // ---------- Utility functions (unchanged) ----------
  const formatDate = (dateValue) => {
    if (!dateValue) return "—";
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "Invalid date";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getOrderNumber = (notification) => {
    const campaign = notification.campaign_name || "";
    const params = notification.notification_content?.templateParams || [];
    switch (campaign) {
      case "pos":
        return params[1] || "N/A";
      case "03_order_confirmation":
      case "online_booking_rejection":
      case "01_booking_reschedule_with_form":
      case "02_order_completion":
      case "boarding_booking_confirmation":
        return params[2] || "N/A";
      case "room_updation_mess":
        return params[5] || "N/A";
      default:
        return params[2] || "N/A";
    }
  };

  const formatMessageText = (notification) => {
    const campaign = notification.campaign_name || "";
    const params = notification.notification_content?.templateParams || [];
    const [p1, p2, p3, p4, p5, p6, p7] = params;
    switch (campaign) {
      case "pos":
        return `Order Successful!\n\nHi, your order has been successfully placed with ${p1 || "Vendor"}.\nOrder: ${p2 || "N/A"}\nAmount: ${p3 || "0"}\nPayment Status: ${p4 || "Unpaid"}\n\nThank you!`;
      case "03_order_confirmation":
        return `Booking Confirmed!\n\nHi ${p1 || "Customer"}, your booking with ${p2 || "Vendor"} has been confirmed.\nOrder: ${p3 || "N/A"}\n${p4 || ""}\nAmount: ${p5 || "0"}\nPayment Status: ${p6 || "unpaid"}${p7 ? "\nPlease complete the required forms before your appointment:" : ""}\n\nThank you for choosing us!`;
      case "online_booking_rejection":
        return `Booking Rejected!\n\nHi ${p1 || "Customer"}, your booking request with ${p2 || "Vendor"} has been Rejected.\nOrder: ${p3 || "N/A"}\nService Date: ${p4 || "N/A"}\n\nWe apologize for the inconvenience.\n\nThank you for choosing us!`;
      case "01_booking_reschedule_with_form":
        return `Booking Rescheduled!\n\nHi ${p1 || "Customer"}, your booking with ${p2 || "Vendor"} has been Rescheduled.\nOrder: ${p3 || "N/A"}\n${p4 || ""}\nAmount: ${p5 || "0"}\nPayment Status: ${p6 || "unpaid"}${p7 ? "\nPlease complete the required forms before your appointment:" : ""}\n\nThank you for choosing us!`;
      case "02_order_completion":
        return `Service Completed!\n\nHi ${p1 || "Customer"}, your booking with ${p2 || "Vendor"} has been successfully completed.\nOrder: ${p3 || "N/A"}\n${p4 || ""}\nAmount: ${p5 || "0"}\nPayment Status: ${p6 || "unpaid"}\n\nPlease take a moment to share your feedback.\n\nThank you for choosing us!`;
      case "room_updation_mess":
        return `Room Updated!\n\nHi ${p1 || "Customer"}, your room for ${p3 || "service"} has been updated.\nPet: ${p2 || "N/A"}\nRoom: ${p4 || "N/A"}\nCheck-in: ${p5 || "N/A"}\nCheck-out: ${p6 || "N/A"}\nAmount: ${p7 || "0"}\n\nThank you for choosing us!`;
      case "boarding_booking_confirmation":
        return `Boarding Confirmed!\n\nHi ${p1 || "Customer"}, your boarding booking with ${p3 || "Vendor"} is confirmed.\nPet: ${p2 || "N/A"}\nCheck-in: ${p4 || "N/A"}\nCheck-out: ${p5 || "N/A"}\nOrder: ${p6 || "N/A"}\nAmount: ${p7 || "0"}\n\nThank you for choosing us!`;
      case "payment_reminder":
        return `Payment Reminder!\n\nHi ${p1 || "Customer"}, this is a reminder for your payment.\nVendor: ${p2 || "Vendor"}\nService: ${p3 || "N/A"}\nTotal: ${p4 || "0"}\nPaid: ${p5 || "0"}\nDue: ${p6 || "0"}\n\nPlease clear your dues at the earliest.\n\nThank you!`;
      default:
        return (
          `Message (${campaign || "Unknown Campaign"})\n\n` +
          params.map((p, i) => `Param ${i + 1}: ${p}`).join("\n")
        );
    }
  };

  const getButtonInfo = (buttons) => {
    if (!buttons || !Array.isArray(buttons))
      return { hasInvoice: false, hasFeedback: false };
    let hasInvoice = false;
    let hasFeedback = false;
    buttons.forEach((btn) => {
      const param = btn.parameters?.[0]?.text;
      if (param) {
        if (/^\d+$/.test(param)) {
          hasFeedback = true;
        } else {
          hasInvoice = true;
        }
      }
    });
    return { hasInvoice, hasFeedback };
  };

  const handleViewMessage = (row) => {
    setSelectedNotification(row);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNotification(null);
  };

  // ---------- Updated Table Columns ----------
  const columns = [
    {
      key: "customerName",
      header: "Customer Name",
      sortable: true,
      sortValue: (row) => row.notification_content?.userName || "",
      renderCell: (row) => (
        <span className="font-medium text-gray-800">
          {row.notification_content?.userName || "—"}
        </span>
      ),
    },
    {
      key: "phoneNumber",
      header: "Phone Number",
      sortable: true,
      sortValue: (row) => row.notification_content?.destination || "",
      renderCell: (row) => (
        <span className="text-sm text-gray-700">
          {row.notification_content?.destination || "—"}
        </span>
      ),
    },
    {
      key: "orderNumber",
      header: "Order Number",
      sortable: true,
      sortValue: (row) => getOrderNumber(row),
      renderCell: (row) => (
        <span className="text-sm font-medium text-gray-700">
          {getOrderNumber(row)}
        </span>
      ),
    },
    {
      key: "sentAt",
      header: "Date & Time Sent",
      sortable: true,
      renderCell: (row) => (
        <span className="text-gray-700">
          {formatDate(row.sent_date || row.createdAt)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      renderCell: (row) => {
        const status = (row.status || "").toLowerCase();
        const isSent = status === "sent" || status === "completed";
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isSent ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
            }`}
          >
            {isSent ? "Sent" : "Failed"}
          </span>
        );
      },
    },
    {
      key: "action",
      header: "Action",
      sortable: false,
      renderCell: (row) => (
        <button
          onClick={() => handleViewMessage(row)}
          className="text-blue-600 hover:text-blue-800 transition-colors"
          title="View message sent"
          aria-label="View message"
        >
          <FaEye className="text-lg" />
        </button>
      ),
    },
  ];

  // ---------- Modal (unchanged) ----------
  const renderModal = () => {
    if (!showModal || !selectedNotification) return null;
    const content = selectedNotification.notification_content || {};
    const { destination, userName, templateParams, media, buttons } = content;
    const formattedMessage = formatMessageText(selectedNotification);
    const { hasInvoice, hasFeedback } = getButtonInfo(buttons);
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Message Sent
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="mb-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 whitespace-pre-wrap font-mono text-sm text-gray-800">
                {formattedMessage}
              </div>
            </div>
            {(hasInvoice || hasFeedback) && (
              <div className="flex flex-wrap gap-2 mb-4">
                {hasInvoice && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    📄 Invoice sent
                  </span>
                )}
                {hasFeedback && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    📝 Feedback requested
                  </span>
                )}
              </div>
            )}
            <hr className="my-4" />
          </div>
        </div>
      </div>
    );
  };

  // Loading / Error states
  if (loading) {
    return (
      <ThemeMaker>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading reminders...</span>
        </div>
      </ThemeMaker>
    );
  }

  if (error) {
    return (
      <ThemeMaker>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600">Error: {error}</p>
        </div>
      </ThemeMaker>
    );
  }

  // ---------- Main render ----------
  return (
    <ThemeMaker>
      <div className="flex flex-col md:flex-row gap-6 p-4">
        {/* Left Sidebar Menu */}
        <div className="w-full md:w-64 flex-shrink-0 bg-white rounded-lg shadow p-4 h-fit">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
            Filter by Type
          </h2>
          <nav className="space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.key}
                onClick={() => setSelectedKey(item.key)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  selectedKey === item.key
                    ? "bg-blue-50 text-blue-700 font-medium border-l-4 border-blue-600"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Right Content */}
        <div className="flex-1 min-w-0">
          {/* Header with title and filter label */}
          <div className="mb-4">
            <h1 className="text-2xl font-semibold text-blue-900">
              Reminder History
             
            </h1>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 text-center border border-gray-200">
              <div className="text-2xl font-bold text-gray-800">
                {stats.total}
              </div>
              <div className="text-sm text-gray-500">Total Messages</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center border border-green-200">
              <div className="text-2xl font-bold text-green-600">
                {stats.sent}
              </div>
              <div className="text-sm text-gray-500">Sent</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center border border-red-200">
              <div className="text-2xl font-bold text-red-600">
                {stats.failed}
              </div>
              <div className="text-sm text-gray-500">Failed</div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow p-4">
            <DynamicTable
              columns={columns}
              data={filteredData}
              searchable={true}
              showPagination={true}
              showRowsPerPage={false}
              showPageInfo={true}
              emptyMessage={`No reminders found for "${currentFilterLabel}".`}
              initialPageSize={10}
            />
          </div>
        </div>
      </div>

      {renderModal()}
    </ThemeMaker>
  );
};

export default ReminderHistoryTable;
